import React, { useEffect, useState } from "react";
import { Whisper, Popover } from "rsuite";
import EditHourModal from "./EditHourModal";
// import EditStatusModal from "./EditStatusModal";
import { Company } from "../../../../interfaces/Company";
import { Project } from "../../../../interfaces/Project";
import { Status } from "../../../../interfaces/Status";
import { GetHours } from "../../../../interfaces/GetHours";
import EditStatusModal from "./EditStatusModal";
import api from "../../../../services/axios";
import SkeletonTable from "../../../../components/skeleton/table";

interface TableProps {
  data: GetHours[];
  year: number;
  month: number;
  project: Project[];
  companies: Company[];
  daysOfMonth: number[];
  onUpdate: () => void;
}

const status: Status[] = [
  { id: 1, name: "Em Aberto" },
  { id: 2, name: "Em Análise" },
];

// "08:00:00" → "08:00" (robusto a horas sem zero à esquerda).
const fmtHM = (h?: string) => {
  if (!h) return "";
  const parts = h.split(":");
  return parts.length >= 2
    ? `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`
    : h;
};

// interface HourEntryData {
//   Application: string;
//   Hours: {
//     [key: string]: string;
//   };
// }

const HourEntry: React.FC<TableProps> = ({
  data,
  year,
  month,
  companies,
  project,
  daysOfMonth,
  onUpdate,
}) => {
  const [selectedTimeSheetDetailId, setSelectedTimeSheetDetailId] =
    useState<number>();
  const [selectedTimeSheetId, setSelectedTimeSheetId] = useState<number>();
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState<number>(year);
  const [selectedMonth, setSelectedMonth] = useState<number>(month);
  const [editedHour, setEditedHour] = useState<string>("");
  const [dsActivity, setDsActivity] = useState<string>("");
  const [isHourModalOpen, setIsHourModalOpen] = useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<number>(0);
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dataEntry, setDataEntry] = useState<GetHours[]>(data);

  useEffect(() => {
    setDataEntry(data);
  }, [data]);

  const openHourModal = (
    day: number,
    hour: string,
    company: number,
    project: number,
    timeSheetId: number,
    timeSheetDetailId: number,
    dsActivity: string,
    projectTaskId?: number | null,
    projectTaskTitle?: string | null
  ) => {
    setSelectedTimeSheetId(timeSheetId);
    setSelectedTimeSheetDetailId(timeSheetDetailId);
    setSelectedDay(day);
    setSelectedMonth(month);
    setSelectedYear(year);
    setEditedHour(hour);
    setSelectedCompany(company);
    setSelectedProject(project);
    setDsActivity(dsActivity);
    setSelectedTaskId(projectTaskId ?? null);
    setSelectedTaskTitle(projectTaskTitle ?? null);
    setIsHourModalOpen(true);
  };

  const closeHourModal = () => {
    setSelectedTimeSheetDetailId(undefined);
    setEditedHour("00:00");
    setSelectedCompany(0);
    setSelectedProject(0);
    setDsActivity("");
    setIsHourModalOpen(false);
    onUpdate();
  };

  const openStatusModal = (
    company: number,
    project: number,
    status: number,
    timeSheetId: number
  ) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setSelectedCompany(company);
    setSelectedProject(project);
    setSelectedStatus(status);
    setSelectedTimeSheetId(timeSheetId);
    setIsStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setSelectedDay(0);
    setIsStatusModalOpen(false);
  };

  const getWorkedHours = async (
    month: Number,
    year: Number,
    clientId: number | null
  ) => {
    setTimeout(() => {
      api
        .get(
          `/TimeManagement/GetWorkedHours?month=${month}&year=${year}&clientId=${clientId}`
        )
        .then((response) => {
          setDataEntry(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setLoading(false);
        });
    }, 1000);
  };

  const onUpdateSearch = () => {
    setLoading(true);
    getWorkedHours(selectedMonth, selectedYear, selectedCompany);
  };

  const isWeekend = (day: number) => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };

  const statusCls = (s: string) => {
    const v = (s || "").toLowerCase();
    if (v.includes("aberto")) return "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300";
    if (v.includes("anális") || v.includes("analis")) return "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300";
    return "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300";
  };

  return (
    <div className="mt-5 animate-fade-up">
      {loading ? (
        <SkeletonTable active={loading} />
      ) : dataEntry.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-300">
          Nenhuma hora apontada neste período.
        </div>
      ) : (
        <div className="max-h-[72vh] overflow-auto rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgba(112,144,176,0.12)] dark:border-white/10 dark:bg-navy-800">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th scope="col" className="sticky left-0 top-0 z-30 w-[170px] min-w-[170px] border-b border-gray-100 bg-gray-50 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:border-gray-700 dark:bg-navy-900 dark:text-gray-300">
                  Cliente
                </th>
                <th scope="col" className="sticky left-[170px] top-0 z-30 w-[190px] min-w-[190px] border-b border-r border-gray-100 bg-gray-50 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:border-gray-700 dark:bg-navy-900 dark:text-gray-300">
                  Projeto
                </th>
                <th scope="col" className="sticky top-0 z-20 border-b border-gray-100 bg-gray-50 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:border-gray-700 dark:bg-navy-900 dark:text-gray-300">
                  Status
                </th>
                {daysOfMonth.map((day) => (
                  <th
                    key={day}
                    scope="col"
                    className={`sticky top-0 z-20 border-b border-gray-100 px-3 py-3 text-center text-[11px] font-bold tabular-nums dark:border-gray-700 ${
                      isWeekend(day)
                        ? "bg-gray-100 text-gray-500 dark:bg-navy-900 dark:text-gray-500"
                        : "bg-gray-50 text-gray-700 dark:bg-navy-900 dark:text-gray-300"
                    }`}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataEntry.map((entry, dataIndex) => (
                <tr key={dataIndex} className="group">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 border-b border-gray-100 bg-white px-4 py-3 text-left font-semibold text-navy-700 group-hover:bg-gray-50 dark:border-gray-700 dark:bg-navy-800 dark:text-white dark:group-hover:bg-navy-700"
                  >
                    <span className="block max-w-[150px] truncate" title={entry.nmCorporate}>{entry.nmCorporate}</span>
                  </th>
                  <td className="sticky left-[170px] z-10 border-b border-r border-gray-100 bg-white px-4 py-3 text-gray-700 group-hover:bg-gray-50 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-300 dark:group-hover:bg-navy-700">
                    <span className="block max-w-[160px] truncate" title={entry.nmProject}>{entry.nmProject}</span>
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 group-hover:bg-gray-50/50 dark:border-gray-700 dark:group-hover:bg-navy-700/40">
                    <button
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${statusCls(entry.status)}`}
                      onClick={() =>
                        openStatusModal(entry.clientId, entry.projectId, entry.statusId, entry.timeSheetId)
                      }
                    >
                      {entry.status}
                    </button>
                  </td>
                  {daysOfMonth.map((day) => {
                    // Todos os lançamentos do dia (pode haver mais de um p/ o mesmo cliente/projeto).
                    const dayDetails = entry.workedHoursDetail.filter((h) => h.dayRef === day);
                    return (
                      <td
                        key={day}
                        className={`border-b border-gray-100 px-2 py-3 text-center dark:border-gray-700 ${
                          isWeekend(day)
                            ? "bg-gray-50/60 group-hover:bg-gray-100/60 dark:bg-navy-900/40 dark:group-hover:bg-navy-700/40"
                            : "group-hover:bg-gray-50/50 dark:group-hover:bg-navy-700/40"
                        }`}
                      >
                        {dayDetails.length > 0 ? (
                          <div className="flex flex-col items-center gap-1">
                            {dayDetails.map((detail) => (
                              <Whisper
                                key={detail.timeSheetDetailId}
                                trigger="hover"
                                placement="top"
                                speaker={
                                  <Popover arrow>
                                    <div className="max-w-[260px]">
                                      <span className="mb-1 inline-block rounded-md bg-dv-blue-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-dv-blue-600">
                                        Tarefa
                                      </span>
                                      <div className="text-[13px] font-bold leading-snug text-navy-700">
                                        {detail.projectTaskTitle || "Sem tarefa vinculada"}
                                      </div>
                                      <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                        Descrição
                                      </div>
                                      <div className="whitespace-pre-line text-xs leading-snug text-gray-600">
                                        {detail.dsActivity || "—"}
                                      </div>
                                    </div>
                                  </Popover>
                                }
                              >
                                <button
                                  className="inline-flex min-w-[44px] items-center justify-center rounded-lg bg-dv-blue-50 px-2 py-1 text-xs font-bold tabular-nums text-dv-blue-600 transition-colors hover:bg-dv-blue-500 hover:text-white dark:bg-dv-blue-500/15 dark:text-dv-cyan-300 dark:hover:bg-dv-blue-500 dark:hover:text-white"
                                  onClick={() =>
                                    openHourModal(
                                      day,
                                      detail.hoursWorked,
                                      entry.clientId,
                                      entry.projectId,
                                      entry.timeSheetId,
                                      detail.timeSheetDetailId,
                                      detail.dsActivity,
                                      detail.projectTaskId,
                                      detail.projectTaskTitle
                                    )
                                  }
                                >
                                  {fmtHM(detail.hoursWorked)}
                                </button>
                              </Whisper>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isHourModalOpen && (
        <EditHourModal
          isModalOpen={isHourModalOpen}
          day={selectedDay}
          month={selectedMonth}
          year={selectedYear}
          initialValue={editedHour}
          company={companies}
          initialCompanyValue={selectedCompany}
          project={project}
          initialProjectValue={selectedProject}
          timeSheetid={selectedTimeSheetId}
          timeSheetDetailId={selectedTimeSheetDetailId}
          dsActivity={dsActivity}
          initialTaskId={selectedTaskId}
          initialTaskTitle={selectedTaskTitle}
          onClose={closeHourModal}
          onUpdate={onUpdateSearch}
        />
      )}

      {isStatusModalOpen && (
        <EditStatusModal
          isModalOpen={isStatusModalOpen}
          company={companies}
          initialCompanyValue={selectedCompany}
          project={project}
          initialProjectValue={selectedProject}
          status={status}
          initialStatusValue={selectedStatus}
          month={selectedMonth}
          timeSheetId={selectedTimeSheetId}
          onClose={closeStatusModal}
          onUpdate={onUpdateSearch}
        />
      )}
    </div>
  );
};

export default HourEntry;
